import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useTheme } from '@mui/material/styles';
import { container } from '@infrastructure/container';
import { useToast } from '@shared/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { AddProductField } from './AddProductField';
import AddProductPartBottomSheet, { ProductPartSelection } from '../parts/AddProductParts';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { Product } from '@domain/product/models/Product';

interface CreateProductFlowProps {
  onClose: () => void;
  onSuccess?: (product: Product) => void;
}

export const CreateProductFlow: React.FC<CreateProductFlowProps> = ({ onClose, onSuccess }) => {
  const theme = useTheme();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const [image, setImage] = useState('');
  const [title, setTitle] = useState('');
  const [ean, setEan] = useState('');
  const [sku, setSku] = useState('');
  const [parts, setParts] = useState<ProductPartSelection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddParts = async (selections: ProductPartSelection[]) => {
    setParts((prev) => {
      const byId = new Map(prev.map((p) => [p.product.id, p]));
      selections.forEach((s) => byId.set(s.product.id, s));
      return Array.from(byId.values());
    });
  };

  const handleRemovePart = (productId: string) => {
    setParts((prev) => prev.filter((p) => p.product.id !== productId));
  };

  const handleOpenAddParts = () => {
    openBottomSheet({
      title: 'Add Parts',
      component: (
        <AddProductPartBottomSheet
          existingPartProductIds={new Set(parts.map((p) => p.product.id))}
          onAdd={handleAddParts}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      showError('Please enter a title');
      return;
    }

    setIsSubmitting(true);
    try {
      const product: Omit<Product, 'id'> = {
        ean: ean.trim(),
        sku: sku.trim(),
        title: title.trim(),
        image: image.trim(),
        purchasable: true,
        parts: parts.map(({ product, quantity }) => ({
          id: product.id,
          part: product,
          quantity,
        })),
      };
      
      const result = await container.productRepository.createProduct(product);
      if (result.status === 'success' && result.data) {
        success('Product created successfully');
        queryClient.invalidateQueries({ queryKey: ['products'] });
        onSuccess?.(result.data);
      }
      onClose();
    } catch {
      showError('Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Create Product</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={3} sx={{ p: 4 }}>
          {/* Image and Title first */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                flexShrink: 0,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
              }}
            >
              {image.trim() ? (
                <Avatar
                  src={image}
                  variant="rounded"
                  sx={{ width: '100%', height: '100%' }}
                  onError={() => setImage('')}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No image
                </Typography>
              )}
            </Box>
            <Stack spacing={2} flex={1}>
              <TextField
                label="Product Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                size="small"
                placeholder="e.g., My Product"
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">Add fields:</Typography>
            <AddProductField label="EAN" value={ean} onChange={(value) => setEan(value)} onRemove={() => setEan('')} />
            <AddProductField label="SKU" value={sku} onChange={(value) => setSku(value)} onRemove={() => setSku('')} />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Parts</Typography>
              <Button variant="text" color="primary" size="small" startIcon={<Add />} onClick={handleOpenAddParts}>
                Add part
              </Button>
            </Stack>
            {parts.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>Quantity</TableCell>
                      <TableCell padding="none" sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parts.map(({ product: partProduct, quantity }) => (
                      <TableRow key={partProduct.id}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                              src={partProduct.image}
                              variant="rounded"
                              sx={{ width: 32, height: 32, border: 1, borderColor: 'divider' }}
                            />
                            <Typography variant="body2">{partProduct.title || partProduct.ean}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{quantity}</TableCell>
                        <TableCell padding="none">
                          <IconButton size="small" onClick={() => handleRemovePart(partProduct.id)}>
                            <Close fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No parts added. Add products that make up this product.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          py: 3,
          px: 4,
          borderTop: 1,
          borderColor: theme.palette.divider,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
          <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!title.trim() || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Product'}
          </Button>
        </Stack>
      </Box>
    </>
  );
};
